import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { DailyBars, DailyLine } from './DashboardCharts';
import { formatShare, share, valueForTeam } from './dashboardData';
import { formatDate, teamLabel } from '../ui/format';

// The Dashboard's "Export PDF": a print-ready page in a new window, printed with the browser dialog.

const PRINT = { bar: '#0E6A86', line: '#1C2228', rule: '#DCE1E5', muted: '#5A646E' };

const STYLE = `
  * { box-sizing: border-box; }
  body { margin: 0; padding: 28px; color: #1C2228; font: 13px/1.45 "Atkinson Hyperlegible Next", -apple-system, "Segoe UI", Arial, sans-serif; }
  .sheet { max-width: 1040px; margin: 0 auto; }
  header { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; padding-bottom: 12px; border-bottom: 2px solid #1C2228; }
  h1 { margin: 0; font-size: 20px; }
  .meta { color: #5A646E; font-size: 12px; }
  section { padding: 16px 0; border-bottom: 1px solid #DCE1E5; break-inside: avoid; }
  h2 { margin: 0 0 8px; font-size: 15px; }
  .cols { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 24px; }
  .cols.two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  table { width: 100%; border-collapse: collapse; }
  th, td { padding: 5px 8px 5px 0; border-bottom: 1px solid #DCE1E5; text-align: left; vertical-align: top; }
  th { font-size: 12px; color: #5A646E; font-weight: 600; }
  td.n { text-align: right; font-variant-numeric: tabular-nums; }
  .id { font-family: "Atkinson Hyperlegible Mono", ui-monospace, Menlo, monospace; }
  .empty { color: #5A646E; }
  .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }
  @media print { body { padding: 0; } }
`;

const esc = (value) => String(value ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const num = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US'));
const days = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(1));

const keyValues = (title, rows) => `
  <div><h2>${esc(title)}</h2><table>${rows
    .filter(Boolean)
    .map(([label, value]) => `<tr><td>${esc(label)}</td><td class="n">${esc(value)}</td></tr>`)
    .join('')}</table></div>`;

const ticketTable = (title, tickets) => `
  <div><h2>${esc(title)}</h2>${
    tickets.length === 0
      ? '<p class="empty">No tickets.</p>'
      : `<table><tr><th>Ticket</th><th>Assignee</th><th>Team</th><th>Status</th><th>Created</th><th>Updated</th></tr>${tickets
        .map((t) => `<tr><td class="id">${esc(t.ticket_name || `Ticket ${t.id}`)}</td><td>${esc(t.assignee || '—')}</td><td>${esc(teamLabel(t.flow_type) || '—')}</td><td>${esc(t.status || '—')}</td><td>${esc(formatDate(t.created_at) || '—')}</td><td>${esc(formatDate(t.updated_at) || '—')}</td></tr>`)
        .join('')}</table>`
  }</div>`;

const statusTable = (title, rows, total, totalLabel) => `
  <div><h2>${esc(title)}</h2><table><tr><td><b>${esc(totalLabel)}</b></td><td class="n"><b>${num(total)}</b></td></tr>${rows
    .map((r) => `<tr><td>${esc(r.status)}</td><td class="n">${num(r.count)}</td></tr>`)
    .join('')}</table></div>`;

// Per-team values the legacy page read; the API doesn't send them today, so their sections only appear once it does.
function optionalSections(stats, team) {
  const sections = [];
  const perTeam = (title, globalValue, map, format) => {
    if (globalValue === undefined && map === undefined) return;
    const rows = team
      ? [[teamLabel(team), format(valueForTeam(map, team))]]
      : [['All teams', format(globalValue)], ...Object.entries(map || {}).map(([flow, v]) => [teamLabel(flow), format(v)])];
    sections.push(keyValues(title, rows));
  };
  perTeam('Days from detection to ticket', stats.avg_ticket_creation_days_global, stats.avg_ticket_creation_days_by_flow, days);
  perTeam('Days from ticket to resolution', stats.avg_ticket_resolve_days_global, stats.avg_ticket_resolve_days_by_flow, days);
  if (stats.ticket_creation_schedule) {
    const sched = stats.ticket_creation_schedule;
    const rows = team ? [[teamLabel(team), valueForTeam(sched, team) || '—']] : Object.entries(sched).map(([k, v]) => [teamLabel(k), v || '—']);
    sections.push(keyValues('Scheduled ticket creation', rows));
  }
  return sections;
}

export default function openDashboardReport({ stats, team, tickets }) {
  const teamName = team ? teamLabel(team) : 'All teams';
  const title = `Dashboard report: ${teamName}`;
  const total = stats.total_vulnerabilities || 0;
  const packages = stats.total_packages || 0;

  const exposure = `
    <section class="cols">
      ${keyValues('Summary', [['Hosts', num(stats.total_hosts)], ['Packages', num(stats.total_packages)], ['Vulnerability findings', num(total)]])}
      ${keyValues('Severity', [
        ['Critical', `${num(stats.critical_vulnerabilities)} (${formatShare(share(stats.critical_vulnerabilities, total))})`],
        ['High', `${num(stats.high_vulnerabilities)} (${formatShare(share(stats.high_vulnerabilities, total))})`],
      ])}
      ${keyValues('Packages', [
        ['Vulnerable', `${num(stats.packages_with_vulnerabilities)} (${formatShare(share(stats.packages_with_vulnerabilities, packages))})`],
        ['Outdated', `${num(stats.outdated_packages)} (${formatShare(share(stats.outdated_packages, packages))})`],
      ])}
    </section>`;

  const hotspots = `
    <section class="cols">
      ${keyValues('Hosts', [
        ['Hosts with vulnerabilities', num(stats.hosts_with_vulnerabilities)],
        ['Most vulnerable host', stats.host_with_most_vulnerabilities?.hostname || '—'],
        ['Hosts in tickets', num(stats.hosts_in_tickets)],
        stats.host_with_most_tickets !== undefined && ['Host with most tickets', stats.host_with_most_tickets || '—'],
      ])}
      ${keyValues('Packages', [
        ['Most vulnerable package', stats.package_with_most_vulnerabilities?.package_name || '—'],
        ['Packages in tickets', num(stats.packages_in_tickets)],
        stats.package_with_most_tickets !== undefined && ['Package with most tickets', stats.package_with_most_tickets || '—'],
      ])}
      ${keyValues('Vulnerabilities', [
        ['Vulnerabilities in tickets', num(stats.vulns_in_tickets)],
        stats.vuln_with_most_tickets !== undefined && ['Vulnerability with most tickets', stats.vuln_with_most_tickets || '—'],
      ])}
    </section>`;

  let remediation = '';
  if (tickets) {
    // As before: with a team selected, "oldest pending" lists tickets in Created status only.
    const oldest = team
      ? tickets.tickets.filter((t) => String(t.status || '').toLowerCase() === 'created')
        .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0)).slice(0, 5)
      : tickets.oldestPending;
    const bars = renderToStaticMarkup(<DailyBars data={tickets.opened} colors={PRINT} label="Tickets opened per day" />);
    const line = renderToStaticMarkup(<DailyLine data={tickets.pendingDaily} colors={PRINT} label="Pending tickets per day" />);
    remediation = `
      <section class="cols two">
        ${statusTable('Tickets by status', tickets.byStatus, tickets.total, 'Total')}
        ${statusTable('Opened in the last 30 days', tickets.byStatusLastMonth, tickets.totalLastMonth, 'Total')}
      </section>
      <section>${ticketTable('Recent tickets', tickets.recent)}</section>
      <section>${ticketTable('Oldest pending tickets', oldest)}</section>
      <section class="cols two">
        <div><h2>Opened per day, last 30 days</h2>${bars}</div>
        <div><h2>Pending at end of day, last 30 days</h2>${line}</div>
      </section>`;
  }

  const optional = optionalSections(stats, team);
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${STYLE}</style></head><body><div class="sheet">
    <header><h1>${esc(title)}</h1><span class="meta">Generated ${esc(new Date().toLocaleString())}</span></header>
    ${exposure}${hotspots}${remediation}${optional.length ? `<section class="cols">${optional.join('')}</section>` : ''}
  </div></body></html>`;

  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  // Some browsers print the window's URL in the footer; a neutral path avoids "about:blank".
  try {
    win.document.title = '';
    win.history?.replaceState?.({}, '', '/');
  } catch (_err) {
    // cross-window access can fail; printing still works
  }
  win.focus();
  setTimeout(() => {
    try {
      win.print();
    } catch (_err) {
      // the user can still print from the window
    }
  }, 250);
  return true;
}
